import styled, { CSSProperties } from 'styled-components';
import { useState, useEffect, ReactNode } from 'react';
import { Text } from './Text';
import { FlexCol } from './Flex';
import IconClose from '../../../public/icons/close.svg?react';

const StyledModalContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	position: fixed;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 100;
`;

const StyledModal = styled(FlexCol)<{ $overflowVisible: boolean }>`
	width: calc(100% - 20px);
	max-width: 560px;
	height: auto;
	max-height: calc(100% - 60px);

	padding: 2rem;

	position: relative;
	overflow-x: hidden;
	overflow-y: ${({ $overflowVisible }) => ($overflowVisible ? 'auto' : 'hidden')};

	z-index: 5;
	border-radius: 18px;

	animation: popup 0.25s;
	transition: background-color var(--transTime) !important;

	background: ${({ theme }) => theme.modal_bg};
	box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.2);
	@keyframes popup {
		0% {
			opacity: 0;
			transform: scale(0.95);
		}
		100% {
			opacity: 1;
			transform: scale(1);
		}
	}
`;

const ModelHeader = styled(FlexCol)`
	margin-bottom: 2rem;
	row-gap: 0.5rem;
`;

const ModalBackground = styled.div`
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 1;
	background-color: ${({ theme }) => theme.modal_backdrop};
	animation: bgFadeIn 0.25s;
	backdrop-filter: blur(4px);
	-webkit-backdrop-filter: blur(4px);
	@keyframes bgFadeIn {
		0% {
			opacity: 0;
		}
		100% {
			opacity: 1;
		}
	}
`;

const StyledClose = styled.div<{ $hideCloseMobile?: boolean }>`
	width: 1.5rem;
	height: 1.5rem;
	position: absolute;
	top: 1.5rem;
	right: 1.5rem;
	cursor: pointer;
	transition: stroke 0.2s !important;
	z-index: 10;
	&:hover {
		svg {
			fill: ${({ theme }) => theme.text_title_main};
		}
	}
	svg {
		height: 100%;
		width: auto;
		fill: ${({ theme }) => theme.text_title_alt};
	}

	@media (max-width: 525px) {
		top: 15px;
		right: 10px;
	}
`;

export type ModalProps = {
	closeModal: () => void;
};

interface ModalExtraProps {
	className?: string;
	style?: CSSProperties;
	title?: string;
	desc?: string;
	children?: ReactNode;
}
export function Modal(props: ModalExtraProps & ModalProps) {
	const { style, title, desc, children, closeModal } = props;

	const [overflowVisible, setOverflowVisible] = useState<boolean>(false);

	useEffect(() => {
		setTimeout(() => setOverflowVisible(true), 250);
	}, []);

	return (
		<StyledModalContainer>
			<StyledModal style={style} $overflowVisible={overflowVisible}>
				{(title || desc) && (
					<ModelHeader>
						{title && (
							<Text $color="text_title_main" $size="2rem" $weight="700">
								{title}
							</Text>
						)}
						{desc && (
							<Text $color="text_title_alt" $size="1.3rem" $weight="400">
								{desc}
							</Text>
						)}
					</ModelHeader>
				)}
				<StyledClose onClick={closeModal}>
					<IconClose />
				</StyledClose>
				{children}
			</StyledModal>
			<ModalBackground onClick={closeModal} />
		</StyledModalContainer>
	);
}
